import { Box, Text, HStack } from '@chakra-ui/react'
import { Badge } from '@apex/component-library'
import { isEmpty, uniqueId } from 'lodash'
import React from 'react'
import { FaFlag } from 'react-icons/fa'
import { Asset } from '../../../../types/Asset'
import { TableColumn } from '@apex/component-library/components/Table/types'
import { Table } from '@apex/component-library'
import { formatAssetAmount } from './formatAssetAmount'
import { CurrencyEnum } from '../../../../utils/numberToCurrency'

const assembleColumn = (
  label: string,
  recordField: string,
  isSortable: boolean,
  alignment?: TableColumn['alignment'],
  isCentered: boolean = false,
  width?: number,
  cellRenderer?: ({
    value,
    record,
  }: {
    value: any
    record: Asset
  }) => JSX.Element
): TableColumn =>
  ({
    key: uniqueId(recordField),
    label,
    recordField,
    isSortable,
    alignment,
    isCentered,
    ...(cellRenderer && { cellRenderer }),
    width,
  } as TableColumn)

interface AssetsTableProps {
  assets: Asset[]
  title: string
}

const AssetsTable = ({ assets, title }: AssetsTableProps) => {
  const assetDisplayValues = new Map()
  const hasAssets = !isEmpty(assets)
  const data = hasAssets
    ? assets.map(asset => {
        assetDisplayValues.set(
          asset.amount + asset.currency,
          formatAssetAmount(asset)
        )
        return {
          key: asset.id,
          symbol: asset.symbol,
          cusip: asset.cusip,
          position: asset.position,
          quantity: asset.quantity,
          description: asset.description,
          amount: asset.amount,
          currency: asset.currency,
          accountType: asset.accountType,
          settlingLocation: asset.settlingLocation,
          settlingLocationReason: asset.settlingLocationReason,
        }
      })
    : [
        {
          key: 1,
          symbol: 'No assets found.',
          cusip: '',
          position: '',
          quantity: '',
          description: '',
          amount: '',
          currency: '',
          accountType: '',
          settlingLocation: '',
          settlingLocationReason: '',
        },
      ]
  const columns: TableColumn[] = [
    assembleColumn('Symbol', 'symbol', hasAssets, 'left', false, 90),
    assembleColumn('CUSIP', 'cusip', hasAssets, 'left', false, 100),
    assembleColumn(
      'Position',
      'position',
      hasAssets,
      'left',
      false,
      90,
      ({ value }: { value: string }) =>
        value ? (
          <Badge
            variant="solid"
            padding="0.25rem 0.75rem"
            colorScheme={value === 'LONG' ? 'green' : 'red'}
          >
            {value}
          </Badge>
        ) : (
          <div />
        )
    ),
    assembleColumn('Quantity', 'quantity', hasAssets, 'right', false, 90),
    assembleColumn('Description', 'description', hasAssets),
    assembleColumn(
      'Amount',
      'amount',
      hasAssets,
      'right',
      false,
      125,
      ({ value, record }: { value: number; record: Asset }) => (
        <Box>{assetDisplayValues.get(value + record.currency)}</Box>
      )
    ),
    assembleColumn(
      'Currency',
      'currency',
      hasAssets,
      'left',
      false,
      90,
      ({ value }: { value: string }) => (
        <HStack>
          <span>{value}</span>
          {value !== CurrencyEnum.usd ? <FaFlag /> : ''}
        </HStack>
      )
    ),
    assembleColumn(
      'Account Type',
      'accountType',
      hasAssets,
      'left',
      false,
      190
    ),
    assembleColumn('Settling Location', 'settlingLocation', hasAssets),
    assembleColumn(
      'Settling Location Reason',
      'settlingLocationReason',
      hasAssets
    ),
  ]
  return (
    <Box data-testid="assets-table">
      <Box bg="gray.600" color="apex.white" p={3}>
        <Text fontWeight="bold">{title}</Text>
      </Box>
      <Table
        columns={columns}
        data={data}
        {...(hasAssets && {
          defaultSort: {
            field: 'settlingLocation',
            ascending: true,
          },
        })}
        maxWidth="100%"
        sx={{ tableLayout: 'fixed' }}
      />
    </Box>
  )
}

export default AssetsTable
